import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "@/lib/utils";
import { BodySmall, H3 } from "@/components/ui/typography";
import { Check, ChevronDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { HTMLAttributes } from "react";
interface CloudProps {
  className?: string;
  size?: "large" | "small";
  position?: "left" | "right";
}
export const Cloud = t0 => {
  const $ = _c(10);
  let className;
  let id;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      id,
      className,
      size: t1,
      position: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = id;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    id = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const size = t1 === undefined ? "large" : t1;
  const position = t2 === undefined ? "left" : t2;
  const sizeClasses = size === "large" ? "h-96 w-96" : "h-64 w-64";
  const opacityClasses = size === "large" ? "opacity-60 blur-3xl" : "opacity-40 blur-2xl";
  const gradientDirection = position === "left" ? "bg-gradient-to-r" : "bg-gradient-to-l";
  const t3 = `from-primary/15 via-primary/8 absolute top-1/2 ${position === "left" ? "left-1/4" : "left-3/4"} ${sizeClasses} -translate-x-1/2 -translate-y-1/2 rounded-full ${gradientDirection} to-transparent ${opacityClasses} ${className ?? ""}`;
  let t4;
  if ($[6] !== id || $[7] !== props || $[8] !== t3) {
    t4 = <div id={id} className={t3} {...props} />;
    $[6] = id;
    $[7] = props;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
interface CloudBackgroundProps {
  className?: string;
  children?: React.ReactNode;
}
export const CloudBackground = t0 => {
  const $ = _c(8);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const t1 = `pointer-events-none absolute inset-0 ${className ?? ""}`;
  let t2;
  if ($[4] !== children || $[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[4] = children;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const VenueTypeItem = t0 => {
  const $ = _c(13);
  const {
    icon: Icon,
    name,
    onClick
  } = t0;
  const t1 = onClick ? "bg-primary-foreground/10 hover:bg-primary-foreground/20 cursor-pointer" : "bg-transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex flex-col gap-5 rounded-xl p-4 transition-all duration-200 ", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = onClick ? "button" : undefined;
  const t4 = onClick ? 0 : undefined;
  let t5;
  if ($[2] !== Icon) {
    t5 = <div className="flex h-7.5 w-7.5  md:h-12 md:w-12"><Icon className="text-primary-foreground h-full w-full" strokeWidth={1} /></div>;
    $[2] = Icon;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== name) {
    t6 = <span className="text-primary-foreground text-start text-lg  md:text-xl leading-tight font-normal">{name}</span>;
    $[4] = name;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== onClick || $[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = <button className={t2} onClick={onClick} role={t3} tabIndex={t4}>{t5}{t6}</button>;
    $[6] = onClick;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
export const Stage = t0 => {
  const $ = _c(37);
  let badge;
  let children;
  let className;
  let index;
  let media;
  let props;
  let title;
  if ($[0] !== t0) {
    ({
      className,
      index,
      badge,
      title,
      media,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = badge;
    $[2] = children;
    $[3] = className;
    $[4] = index;
    $[5] = media;
    $[6] = props;
    $[7] = title;
  } else {
    badge = $[1];
    children = $[2];
    className = $[3];
    index = $[4];
    media = $[5];
    props = $[6];
    title = $[7];
  }
  const t1 = index % 2 === 1 && "lg:grid-flow-dense";
  let t2;
  if ($[8] !== className || $[9] !== t1) {
    t2 = cn("relative grid grid-cols-1 items-center gap-24 lg:grid-cols-2 xl:px-18", t1, className);
    $[8] = className;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = index % 2 === 1 ? "lg:col-start-2" : "";
  let t4;
  if ($[11] !== className || $[12] !== t3) {
    t4 = cn(t3, "space-y-10", className);
    $[11] = className;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = index + 1;
  let t6;
  if ($[14] !== badge || $[15] !== t5) {
    t6 = <div id="stage-badge" className="bg-primary/10 inline-flex min-w-64 items-center justify-center rounded-lg px-9 py-2.5 text-center"><BodySmall className="text-primary font-medium tracking-tight">{t5}. {badge}</BodySmall></div>;
    $[14] = badge;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== title) {
    t7 = <H3 id="stage-title" className="text-foreground mb-9">{title}</H3>;
    $[17] = title;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== children) {
    t8 = <div id="stage-list-container" className="space-y-7">{children}</div>;
    $[19] = children;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t4 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <div id="stage-text-container" className={t4}>{t6}{t7}{t8}</div>;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = index % 2 === 1 && "lg:col-start-1 lg:row-start-1";
  let t11;
  if ($[26] !== t10) {
    t11 = cn("flex h-64 items-center justify-center rounded-lg sm:h-auto", t10);
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== media.alt || $[29] !== media.src || $[30] !== t11) {
    t12 = <img className={t11} src={media.src} alt={media.alt} />;
    $[28] = media.alt;
    $[29] = media.src;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== props || $[33] !== t12 || $[34] !== t2 || $[35] !== t9) {
    t13 = <div className={t2} {...props}>{t9}{t12}</div>;
    $[32] = props;
    $[33] = t12;
    $[34] = t2;
    $[35] = t9;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
};
export const FeatureListItem = t0 => {
  const $ = _c(16);
  const {
    text,
    onClick,
    variant: t1,
    Icon: t2
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const Icon = t2 === undefined ? Check : t2;
  const isInteractive = variant === "interactive" && onClick;
  const t3 = isInteractive && "hover:bg-muted/50 -mx-2 cursor-pointer rounded-md px-2 transition-colors";
  let t4;
  if ($[0] !== t3) {
    t4 = cn("flex items-center gap-5", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = isInteractive ? onClick : undefined;
  const t6 = isInteractive ? "button" : undefined;
  const t7 = isInteractive ? 0 : undefined;
  let t8;
  if ($[2] !== Icon) {
    t8 = <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-teal-100"><Icon className="h-4 w-4 text-teal-900" /></div>;
    $[2] = Icon;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  let t9;
  if ($[4] !== text) {
    t9 = <span className="text-foreground flex-1 text-base leading-relaxed font-semibold">{text}</span>;
    $[4] = text;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  let t10;
  if ($[6] !== isInteractive) {
    t10 = isInteractive && <ChevronDown className="text-foreground h-4 w-4 flex-shrink-0" />;
    $[6] = isInteractive;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== t10 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6 || $[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t11 = <button className={t4} onClick={t5} role={t6} tabIndex={t7}>{t8}{t9}{t10}</button>;
    $[8] = t10;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
};